import { Fragment, useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { EmptyState } from '@/components/EmptyState';
import { KpiCard } from '@/components/KpiCard';
import { PageHeader } from '@/components/PageHeader';
import { PageShell } from '@/components/PageShell';
import { 
  MessageSquare, ThumbsUp, ThumbsDown,
  FileText, Clock, ChevronDown, ChevronRight, Shield,
  EyeOff, AlertTriangle, User, FlaskConical,
  RefreshCw, UserCircle, MapPin, Globe, Tag
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/UserAvatar';
import { MarkdownContent } from '@/components/MarkdownContent';

// Eva logo URL
const EVA_LOGO_URL = "/eva-icon-square.png";
const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Format relative time
const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Format timestamp for display
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const parseTestProfile = (session) => {
  try {
    return session.test_profile
      ? (typeof session.test_profile === 'string' ? JSON.parse(session.test_profile) : session.test_profile)
      : null;
  } catch (_e) {
    return null;
  }
};

const getSessionUserLabel = (session) => {
  const profile = parseTestProfile(session);
  if (profile?.first_name || profile?.last_name) {
    return [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  }
  if (session.user_name) return session.user_name;
  return session.user_email || 'Anonymous';
};

const getSessionUserIdentity = (session) => {
  const profile = parseTestProfile(session);
  const name = profile?.first_name || profile?.last_name
    ? [profile.first_name, profile.last_name].filter(Boolean).join(' ')
    : session.user_name || session.user_email || 'Anonymous';
  return {
    name,
    email: session.user_email,
    avatar_url: session.avatar_url,
  };
};

const getSessionLocation = (session) => {
  const profile = parseTestProfile(session);
  return [session.country || profile?.country, session.region || profile?.region].filter(Boolean).join(', ') || '—';
};

const getSessionFeedbackLabel = (session) => {
  if (session.turns?.some(t => t.feedback === 'negative')) return 'Needs review';
  if (session.turns?.some(t => t.feedback === 'positive')) return 'Helpful';
  return 'No feedback';
};

const getSortValue = (session, key) => {
  if (key === 'user') return getSessionUserLabel(session).toLowerCase();
  if (key === 'location') return getSessionLocation(session).toLowerCase();
  if (key === 'turns') return session.turn_count || session.turns?.length || 0;
  if (key === 'feedback') return getSessionFeedbackLabel(session);
  if (key === 'type') return session.is_test ? 'test' : 'regular';
  return new Date(session.started_at || session.last_activity || 0).getTime();
};

// Single conversation turn (user message + Eva response)
function ConversationTurn({ turn, index }) {
  const hasPositiveFeedback = turn.feedback === 'positive';
  const hasNegativeFeedback = turn.feedback === 'negative';
  let sources = [];
  
  try {
    sources = typeof turn.sources === 'string' ? JSON.parse(turn.sources) : (turn.sources || []);
  } catch (_e) {
    sources = [];
  }
  
  return (
    <div className="space-y-3 py-3 border-b border-border last:border-0" data-testid={`turn-${index}`}>
      {/* User Message */}
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-foreground">User</p>
            {turn.timestamp && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTimestamp(turn.timestamp)}
              </span>
            )}
          </div>
          <div className="bg-primary/5 rounded-lg px-3 py-2">
            <p className="text-sm text-foreground">{turn.query}</p>
          </div>
        </div>
      </div>
      
      {/* Eva Response */}
      <div className="flex gap-3">
        <img src={EVA_LOGO_URL} alt="Eva" className="w-8 h-8 rounded-full flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-foreground">Eva</p>
            <div className="flex items-center gap-2">
              {turn.response_timestamp && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTimestamp(turn.response_timestamp)}
                </span>
              )}
              {/* Feedback Badge */}
              {hasPositiveFeedback && (
                <Badge className="bg-success/10 text-success text-xs">
                  <ThumbsUp className="w-3 h-3 mr-1" />
                  Helpful
                </Badge>
              )}
              {hasNegativeFeedback && (
                <Badge className="bg-destructive/10 text-destructive text-xs">
                  <ThumbsDown className="w-3 h-3 mr-1" />
                  Not Helpful
                </Badge>
              )}
            </div>
          </div>
          <div className="bg-secondary dark:bg-card rounded-lg px-3 py-2">
            <MarkdownContent text={turn.response} className="text-foreground" />
          </div>
          
          {/* Sources */}
          {sources.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {sources.slice(0, 3).map((source, idx) => (
                <Badge key={idx} variant="outline" className="text-xs text-muted-foreground">
                  <FileText className="w-3 h-3 mr-1" />
                  {source.title?.substring(0, 30) || 'Document'}
                  {source.title?.length > 30 ? '...' : ''}
                </Badge>
              ))}
              {sources.length > 3 && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  +{sources.length - 3} more
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Session card showing all turns
function SessionCard({ session, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  // Parse test profile if present
  let testProfile = null;
  try {
    testProfile = session.test_profile 
      ? (typeof session.test_profile === 'string' ? JSON.parse(session.test_profile) : session.test_profile)
      : null;
  } catch (_e) {
    testProfile = null;
  }
  
  const turnCount = session.turn_count || session.turns?.length || 0;
  const hasFeedback = session.turns?.some(t => t.feedback);
  const hasPositiveFeedback = session.turns?.some(t => t.feedback === 'positive');
  const hasNegativeFeedback = session.turns?.some(t => t.feedback === 'negative');
  
  // Topic classifications
  const topics = session.topics || [];
  const primaryTopic = topics[0];
  
  return (
    <Card className="overflow-hidden" data-testid={`session-${session.session_id}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full text-left">
          <div className="p-4 hover:bg-secondary/80 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {/* Expand/Collapse Icon */}
                <div className="mt-1">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                
                {/* Session Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {/* Test Badge */}
                    {session.is_test && (
                      <Badge className="bg-warning/10 text-warning text-xs">
                        <FlaskConical className="w-3 h-3 mr-1" />
                        Test
                      </Badge>
                    )}
                    
                    {/* Topic Badge */}
                    {primaryTopic && (
                      <Badge 
                        variant="outline" 
                        className="text-xs"
                        style={{ 
                          borderColor: primaryTopic.topic_color || '#6366f1',
                          backgroundColor: `${primaryTopic.topic_color || '#6366f1'}15`,
                          color: primaryTopic.topic_color || '#6366f1'
                        }}
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {primaryTopic.topic_name}
                        {primaryTopic.sub_topic_name && ` › ${primaryTopic.sub_topic_name}`}
                      </Badge>
                    )}
                    
                    {/* Emerging Topic Badge */}
                    {primaryTopic?.emerging_topic_name && (
                    <Badge className="bg-primary/10 text-primary text-xs">
                        Emerging: {primaryTopic.emerging_topic_name}
                      </Badge>
                    )}
                    
                    {/* User/Profile Info */}
                    {testProfile ? (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <UserCircle className="w-3 h-3" />
                        {testProfile.first_name} {testProfile.last_name}
                        {testProfile.country && (
                          <>
                            <MapPin className="w-3 h-3 ml-2" />
                            {testProfile.country}
                          </>
                        )}
                      </span>
                    ) : session.user_email ? (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <UserAvatar user={getSessionUserIdentity(session)} size="xs" />
                        {session.user_email}
                        {(session.country || session.region) && (
                          <>
                            <MapPin className="w-3 h-3 ml-2" />
                            {[session.country, session.region].filter(Boolean).join(', ')}
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        <EyeOff className="w-3 h-3 inline mr-1" />
                        Anonymous
                      </span>
                    )}
                    
                    {/* Turn count */}
                    <Badge variant="outline" className="text-xs">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      {turnCount} turn{turnCount !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  {/* Preview of first message */}
                  <p className="text-sm text-foreground line-clamp-1">
                    {session.turns?.[0]?.query || 'No messages'}
                  </p>
                  
                  {/* Timestamp */}
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(session.started_at || session.last_activity)}
                  </p>
                </div>
              </div>
              
              {/* Feedback Summary */}
              <div className="flex flex-col items-end gap-1">
                {hasPositiveFeedback && (
                  <Badge className="bg-success/10 text-success text-xs">
                    <ThumbsUp className="w-3 h-3" />
                  </Badge>
                )}
                {hasNegativeFeedback && (
                  <Badge className="bg-destructive/10 text-destructive text-xs">
                    <ThumbsDown className="w-3 h-3" />
                  </Badge>
                )}
                {!hasFeedback && (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    No feedback
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-border">
            {/* Topic Classifications */}
            {topics.length > 0 && (
              <div className="mt-3 p-3 bg-secondary dark:bg-card/50 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Topic Classification
                </p>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="text-xs"
                      style={{
                        borderColor: topic.topic_color || '#6366f1',
                        backgroundColor: `${topic.topic_color || '#6366f1'}10`,
                      }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full mr-1.5" 
                        style={{ backgroundColor: topic.topic_color || '#6366f1' }}
                      />
                      {topic.topic_name}
                      {topic.sub_topic_name && (
                        <span className="text-muted-foreground ml-1">› {topic.sub_topic_name}</span>
                      )}
                      <span className="ml-2 text-muted-foreground">
                        {Math.round((topic.confidence || 0) * 100)}%
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Test Profile Details */}
            {testProfile && (
              <div className="mt-3 p-3 bg-warning/5 rounded-lg border border-warning/20">
                <p className="text-xs font-medium text-warning mb-2">
                  <FlaskConical className="w-3 h-3 inline mr-1" />
                  Test Profile
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-warning/80">
                  {testProfile.first_name && (
                    <span>Name: {testProfile.first_name} {testProfile.last_name}</span>
                  )}
                  {testProfile.title && <span>Title: {testProfile.title}</span>}
                  {testProfile.department && <span>Dept: {testProfile.department}</span>}
                  {testProfile.country && <span>Country: {testProfile.country}</span>}
                  {testProfile.region && <span>Region: {testProfile.region}</span>}
                  {testProfile.office && <span>Office: {testProfile.office}</span>}
                </div>
              </div>
            )}
            
            {/* Conversation Turns - sorted oldest first */}
            <div className="mt-3">
              {session.turns?.length > 0 ? (
                [...session.turns]
                  .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))
                  .map((turn, idx) => (
                    <ConversationTurn key={turn.id || idx} turn={turn} index={idx} />
                  ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No messages in this session
                </p>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function ChatThreadsPage({
  tenantId: embeddedTenantId = null,
  embedded = false,
  tenantInfo: embeddedTenantInfo = null,
  activeSessionCount = 0,
  afterKpis = null,
}) {
  const { user, isSuperadmin } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(embeddedTenantInfo);
  
  // Get tenant from URL query parameter (for superadmin viewing specific tenant)
  const searchParams = new URLSearchParams(window.location.search);
  const rawTenantParam = searchParams.get('tenant');
  const tenantIdParam = embeddedTenantId || (rawTenantParam && rawTenantParam !== 'undefined' && rawTenantParam !== 'null' ? rawTenantParam : null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortKey, setSortKey] = useState('started_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  
  // Only superadmins can view conversation threads (privacy protection)
  const isSuperadminViewer = !!user?.is_superadmin;
  
  useEffect(() => {
    if (isSuperadminViewer) {
      loadSessions();
      loadStats();
      if (tenantIdParam && isSuperadmin && !embeddedTenantInfo) {
        loadTenantInfo(tenantIdParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadminViewer, tenantIdParam, embeddedTenantInfo]);

  useEffect(() => {
    if (embeddedTenantInfo) {
      setTenantInfo(embeddedTenantInfo);
    }
  }, [embeddedTenantInfo]);
  
  const loadTenantInfo = async (tenantId) => {
    try {
      const response = await api.get(`/superadmin/tenants/${tenantId}`);
      setTenantInfo(response.data);
    } catch (err) {
      console.error('Failed to load tenant info:', err);
    }
  };
  
  const loadSessions = async (countryParam = null, regionParam = null) => {
    setLoading(true);
    try {
      let url = '/conversations/all-sessions?include_test=true&limit=200';
      if (countryParam && countryParam !== 'all') {
        url += `&country=${encodeURIComponent(countryParam)}`;
      }
      if (regionParam && regionParam !== 'all') {
        url += `&region=${encodeURIComponent(regionParam)}`;
      }
      // Add tenant filter for superadmin
      if (tenantIdParam && isSuperadmin) {
        url += `&tenant_id=${encodeURIComponent(tenantIdParam)}`;
      }
      
      const res = await api.get(url);
      setSessions(res.data.sessions || []);
      
      // Update available filters from response
      if (res.data.filters) {
        setAvailableCountries(res.data.filters.available_countries || []);
        setAvailableRegions(res.data.filters.available_regions || []);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Only Platform Superadmins can view conversation threads.');
      } else {
        setError('Failed to load conversation sessions');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Reload when country/region filter changes
  const handleCountryChange = (value) => {
    setCountryFilter(value);
    loadSessions(value, regionFilter);
  };
  
  const handleRegionChange = (value) => {
    setRegionFilter(value);
    loadSessions(countryFilter, value);
  };
  
  const loadStats = async () => {
    try {
      const res = await api.get('/conversations/threads/stats/summary?days=30');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };
  
  // Filter sessions
  const filteredSessions = useMemo(() => sessions.filter(session => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesQuery = session.turns?.some(t => 
        t.query?.toLowerCase().includes(searchLower) ||
        t.response?.toLowerCase().includes(searchLower)
      );
      const matchesEmail = session.user_email?.toLowerCase().includes(searchLower);
      if (!matchesQuery && !matchesEmail) return false;
    }
    
    // Type filter (test vs regular)
    if (typeFilter === 'test' && !session.is_test) return false;
    if (typeFilter === 'regular' && session.is_test) return false;
    
    // Feedback filter
    if (feedbackFilter !== 'all') {
      const hasFeedback = session.turns?.some(t => t.feedback);
      if (feedbackFilter === 'with' && !hasFeedback) return false;
      if (feedbackFilter === 'without' && hasFeedback) return false;
    }
    
    return true;
  }), [sessions, searchQuery, typeFilter, feedbackFilter]);

  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) => {
      const aValue = getSortValue(a, sortKey);
      const bValue = getSortValue(b, sortKey);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredSessions, sortKey, sortDirection]);

  const totalPages = Math.max(Math.ceil(sortedSessions.length / pageSize), 1);
  const pagedSessions = sortedSessions.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, typeFilter, feedbackFilter, countryFilter, regionFilter, pageSize]);

  const updateSort = (key) => {
    if (sortKey === key) {
      setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'started_at' ? 'desc' : 'asc');
  };
  
  // Calculate summary stats
  const totalSessions = sessions.length;
  const testSessions = sessions.filter(s => s.is_test).length;
  const sessionsWithFeedback = sessions.filter(s => s.turns?.some(t => t.feedback)).length;
  
  if (!isSuperadminViewer) {
    const restrictedContent = (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              Conversation threads are only visible to Platform Superadmins for privacy reasons.
            </p>
          </CardContent>
        </Card>
    );
    return embedded
      ? <div className="space-y-6" data-testid="chat-threads-page">{restrictedContent}</div>
      : <PageShell maxWidth="7xl" data-testid="chat-threads-page">{restrictedContent}</PageShell>;
  }
  
  const pageContent = (
    <>
      {!embedded && (
        <PageHeader
        title="Conversation Threads"
        description={
          tenantInfo && isSuperadmin
            ? `Viewing ${tenantInfo.display_name} threads with superadmin access.`
            : 'Search, filter, and inspect employee conversations.'
        }
        icon={MessageSquare}
        actions={(
          <Button variant="outline" onClick={() => loadSessions(countryFilter, regionFilter)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
        />
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {embedded ? (
          <>
            <KpiCard title="Active Sessions" value={activeSessionCount} icon={Shield} color="info" />
            <KpiCard title="Conversation Threads" value={totalSessions} icon={MessageSquare} color="primary" />
          </>
        ) : (
          <>
            <KpiCard title="Total Sessions" value={totalSessions} icon={MessageSquare} color="primary" />
            <KpiCard title="Test Sessions" value={testSessions} icon={FlaskConical} color="warning" />
          </>
        )}
        <KpiCard title="With Feedback" value={sessionsWithFeedback} icon={ThumbsUp} color="success" />
        <KpiCard title="Satisfaction Rate" value={`${stats?.feedback?.satisfaction_rate || 0}%`} icon={ThumbsUp} color="success" />
      </div>

      {afterKpis}

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-2" />
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardHeader className="gap-3">
          <div>
            <CardTitle>Threads</CardTitle>
            <CardDescription>Expand a row to inspect conversation turns, sources, topics, and feedback.</CardDescription>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-info/20 bg-info/5 p-3">
            <Shield className="w-5 h-5 text-info mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-info">Privacy Protected</p>
              <p className="text-xs text-info/80 mt-0.5">
                {user?.is_superadmin
                  ? 'As a Superadmin, you have full access to conversation data for support and troubleshooting purposes.'
                  : 'User identities are anonymized. You can analyze patterns without identifying individual employees.'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTableToolbar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search conversations..."
            searchTestId="search-input"
            className="rounded-lg"
          >
            <Select value={regionFilter} onValueChange={handleRegionChange}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="region-filter">
                <MapPin className="w-4 h-4 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {availableRegions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={countryFilter} onValueChange={handleCountryChange}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="country-filter">
                <Globe className="w-4 h-4 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {availableCountries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="test">Test Only</SelectItem>
                <SelectItem value="regular">Regular Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={feedbackFilter} onValueChange={setFeedbackFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="feedback-filter">
                <SelectValue placeholder="Feedback" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Feedback</SelectItem>
                <SelectItem value="with">With Feedback</SelectItem>
                <SelectItem value="without">Without Feedback</SelectItem>
              </SelectContent>
            </Select>
          </DataTableToolbar>

          {(countryFilter !== 'all' || regionFilter !== 'all') && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {regionFilter !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  Region: {regionFilter}
                  <button
                    type="button"
                    onClick={() => handleRegionChange('all')}
                    className="ml-1 hover:text-foreground"
                  >×</button>
                </Badge>
              )}
              {countryFilter !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  Country: {countryFilter}
                  <button
                    type="button"
                    onClick={() => handleCountryChange('all')}
                    className="ml-1 hover:text-foreground"
                  >×</button>
                </Badge>
              )}
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title={sessions.length === 0 ? 'No conversations yet' : 'No conversations match your filters'}
              description={
                sessions.length === 0
                  ? 'Start chatting with Eva to see threads here.'
                  : 'Try adjusting your search, region, country, type, or feedback filters.'
              }
              variant="plain"
            />
          ) : (
            <>
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>
                        <DataTableSortHeader label="User" sortKey="user" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                      <TableHead>
                        <DataTableSortHeader label="Preview" sortKey="started_at" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                      <TableHead>
                        <DataTableSortHeader label="Location" sortKey="location" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                      <TableHead className="text-center">
                        <DataTableSortHeader label="Turns" sortKey="turns" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} align="center" />
                      </TableHead>
                      <TableHead>
                        <DataTableSortHeader label="Type" sortKey="type" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                      <TableHead>
                        <DataTableSortHeader label="Feedback" sortKey="feedback" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                      <TableHead>
                        <DataTableSortHeader label="Last Activity" sortKey="started_at" activeSortKey={sortKey} sortDirection={sortDirection} onSort={updateSort} />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedSessions.map((session) => {
                      const isExpanded = expandedSessionId === session.session_id;
                      const primaryTopic = session.topics?.[0];
                      return (
                        <Fragment key={session.session_id}>
                          <TableRow key={session.session_id} className="cursor-pointer" onClick={() => setExpandedSessionId(isExpanded ? null : session.session_id)}>
                            <TableCell>
                              {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <UserAvatar user={getSessionUserIdentity(session)} size="sm" />
                                <div className="min-w-0">
                                  <div className="truncate font-medium text-foreground">{getSessionUserLabel(session)}</div>
                                  {session.user_email && <div className="truncate text-xs text-muted-foreground">{session.user_email}</div>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="max-w-md">
                              <p className="truncate text-sm text-foreground">{session.turns?.[0]?.query || 'No messages'}</p>
                              {primaryTopic && (
                                <p className="mt-1 truncate text-xs text-muted-foreground">{primaryTopic.topic_name}{primaryTopic.sub_topic_name ? ` / ${primaryTopic.sub_topic_name}` : ''}</p>
                              )}
                            </TableCell>
                            <TableCell>{getSessionLocation(session)}</TableCell>
                            <TableCell className="text-center">{session.turn_count || session.turns?.length || 0}</TableCell>
                            <TableCell>
                              {session.is_test ? (
                                <Badge className="bg-warning/10 text-warning">Test</Badge>
                              ) : (
                                <Badge variant="outline">Regular</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{getSessionFeedbackLabel(session)}</Badge>
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {formatRelativeTime(session.started_at || session.last_activity)}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow key={`${session.session_id}-details`}>
                              <TableCell colSpan={8} className="bg-muted/20 p-4">
                                <SessionCard session={session} defaultOpen />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                totalItems={sortedSessions.length}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                itemLabel="threads"
                pageSizeTestId="threads-page-size"
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  );

  return embedded
    ? <div className="space-y-6" data-testid="chat-threads-page">{pageContent}</div>
    : <PageShell maxWidth="7xl" data-testid="chat-threads-page">{pageContent}</PageShell>;
}
